/**
 * SalaryDataMain Component
 * 
 * Main container component for salary data management operations.
 * Provides a tabbed interface for managing both office employee and construction
 * worker salary data with three main functions for each worker type.
 * 
 * Features:
 * - Dual worker type tabs: Office employees (本社) and Construction workers (現場)
 * - Three function tabs for each worker type:
 *   - Data Upload (データアップロード): File upload and validation
 *   - Data Display (データ表示): Viewing and managing existing data
 *   - CSV Download (CSVダウンロード): Exporting data in CSV format
 * - Responsive design with separate mobile and desktop layouts
 * - Automatic scroll-to-top when switching tabs
 * - Mobile detection and responsive rendering
 * - Japanese language interface
 * 
 * Layout structure:
 * - Mobile: Simplified layout without container wrappers
 * - Desktop: Nested tab structure with container wrappers
 * 
 * Used by:
 * - HomePage layout as a main page component
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import './css/SalaryDataMain.css';
import { DataUpload } from './components/upload/DataUpload';
import { DataDisplay } from './components/display/DataDisplay';
import { CsvDownload } from './components/download/CsvDownload';
import { ConstructionDataUpload } from './components/upload/ConstructionDataUpload';
import { ConstructionDataDisplay } from './components/display/ConstructionDataDisplay';
import { ConstructionCsvDownload } from './components/download/ConstructionCsvDownload';

/**
 * SalaryDataMain Component
 * 
 * Main container component for salary data management.
 * Manages tab navigation, responsive layout, and renders appropriate
 * components based on selected worker type and function.
 * 
 * @returns React functional component
 */
export const SalaryDataMain: React.FC<{ isConstructionAdminOnly?: boolean }> = ({ isConstructionAdminOnly = false }) => {
  const [activeWorkerTab, setActiveWorkerTab] = useState(isConstructionAdminOnly ? 'construction' : 'office');
  const [activeFunctionTab, setActiveFunctionTab] = useState('upload');
  const [isMobile, setIsMobile] = useState(false);

  /**
   * Handles worker type tab change (office vs. construction).
   * 
   * Updates the active worker tab and scrolls to top of page.
   * This ensures users see the top of the new tab content immediately.
   * 
   * @param tab - The worker type tab to activate ('office' or 'construction')
   * 
   * @example
   * ```typescript
   * handleWorkerTabChange('construction'); // Switch to construction worker tab
   * ```
   */
  const handleWorkerTabChange = (tab: string) => {
    setActiveWorkerTab(tab);
    /**
     * Scroll to top when changing tabs.
     * Uses multiple methods for cross-browser compatibility.
     */
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  /**
   * Handles function tab change (upload, display, or download).
   * 
   * Updates the active function tab and scrolls to top of page.
   * This ensures users see the top of the new function content immediately.
   * 
   * @param tab - The function tab to activate ('upload', 'display', or 'download')
   * 
   * @example
   * ```typescript
   * handleFunctionTabChange('display'); // Switch to data display tab
   * ```
   */
  const handleFunctionTabChange = (tab: string) => {
    setActiveFunctionTab(tab);
    /**
     * Scroll to top when changing tabs.
     * Uses multiple methods for cross-browser compatibility.
     */
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  /**
   * Check for mobile device and scroll to top on mount.
   * 
   * Detects mobile devices based on window width (<= 768px) and sets
   * the isMobile state accordingly. Also sets up resize listener to
   * update mobile state when window is resized. Scrolls to top of page
   * when component mounts.
   * 
   * @effect
   * Runs once on mount.
   */
  useEffect(() => {
    /**
     * Check if device is mobile based on window width.
     * Mobile threshold: 768px (tablet and below).
     */
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    /**
     * Initial mobile check on mount.
     */
    checkMobile();
    
    /**
     * Add resize listener to update mobile state on window resize.
     */
    window.addEventListener('resize', checkMobile);
    
    /**
     * Scroll to top when component mounts.
     * Uses multiple methods for cross-browser compatibility.
     */
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    /**
     * Cleanup: Remove resize listener on unmount.
     */
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Mobile Layout - Simplified layout without container wrappers.
   * 
   * Renders a simplified layout optimized for mobile devices (<= 768px width).
   * Uses mobile-layout class for mobile-specific styling.
   */
  if (isMobile) {
    return (
      <div className="main-content mobile-layout">
        {/* Main Page Title - "Salary Data Management" */}
        <h3>給与データ管理</h3>
        
        {/* Worker Type Tab Navigation - Office (本社) vs. Construction (現場); 現場管理者 sees only 現場 */}
        <nav className="tab-navigation">
          {!isConstructionAdminOnly && (
            <button 
              className={`tab-button ${activeWorkerTab === 'office' ? 'active' : ''}`}
              onClick={() => handleWorkerTabChange('office')}
            >
              本社
            </button>
          )}
          <button 
            className={`tab-button ${activeWorkerTab === 'construction' ? 'active' : ''}`}
            onClick={() => handleWorkerTabChange('construction')}
          >
            現場
          </button>
        </nav>
        
        {/* Function Tab Navigation - Upload, Display, Download */}
        <nav className="sub-tab-navigation">
          {/* Data Upload Tab - File upload and validation */}
          <button 
            className={`sub-tab-button ${activeFunctionTab === 'upload' ? 'active' : ''}`}
            onClick={() => handleFunctionTabChange('upload')}
          >
            データアップロード
          </button>
          
          {/* Data Display Tab - Viewing and managing existing data */}
          <button 
            className={`sub-tab-button ${activeFunctionTab === 'display' ? 'active' : ''}`}
            onClick={() => handleFunctionTabChange('display')}
          >
            データ表示
          </button>
          
          {/* CSV Download Tab - Exporting data in CSV format */}
          <button 
            className={`sub-tab-button ${activeFunctionTab === 'download' ? 'active' : ''}`}
            onClick={() => handleFunctionTabChange('download')}
          >
            CSVダウンロード
          </button>
        </nav>
        
        {/* Function Tab Content Area - Renders active function component */}
        <div className="sub-tab-content">
          {/* Data Upload Tab Panel */}
          {activeFunctionTab === 'upload' && (
            <div className="sub-tab-panel">
              {/* Conditionally render office or construction upload component */}
              {activeWorkerTab === 'office' ? (
                <DataUpload />
              ) : (
                <ConstructionDataUpload />
              )}
            </div>
          )}
          
          {/* Data Display Tab Panel */}
          {activeFunctionTab === 'display' && (
            <div className="sub-tab-panel">
              {/* Conditionally render office or construction display component */}
              {activeWorkerTab === 'office' ? (
                <DataDisplay />
              ) : (
                <ConstructionDataDisplay />
              )}
            </div>
          )}
          
          {/* CSV Download Tab Panel */}
          {activeFunctionTab === 'download' && (
            <div className="sub-tab-panel">
              {/* Conditionally render office or construction download component */}
              {activeWorkerTab === 'office' ? (
                <CsvDownload />
              ) : (
                <ConstructionCsvDownload />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * Desktop Layout - Full layout with container wrappers.
   * 
   * Renders the full desktop layout with nested tab structure.
   * Includes container wrappers for better styling and organization.
   */
  return (
    <div className="main-content">
      {/* Main Page Title - "Salary Data Management" */}
      <h3>給与データ管理</h3>
      
      {/* Main Tab Container - Contains worker type tabs and nested function tabs */}
      <div className="tab-container">
        <nav className="tab-navigation">
          {!isConstructionAdminOnly && (
            <button 
              className={`tab-button ${activeWorkerTab === 'office' ? 'active' : ''}`}
              onClick={() => handleWorkerTabChange('office')}
            >
              本社
            </button>
          )}
          <button 
            className={`tab-button ${activeWorkerTab === 'construction' ? 'active' : ''}`}
            onClick={() => handleWorkerTabChange('construction')}
          >
            現場
          </button>
        </nav>
        
        {/* Function Tab Container - Nested within worker type tabs */}
        <div className="tab-content">
          {/* Function Tab Navigation - Upload, Display, Download */}
          <nav className="sub-tab-navigation">
            {/* Data Upload Tab - File upload and validation */}
            <button 
              className={`sub-tab-button ${activeFunctionTab === 'upload' ? 'active' : ''}`}
              onClick={() => handleFunctionTabChange('upload')}
            >
              データアップロード
            </button>
            
            {/* Data Display Tab - Viewing and managing existing data */}
            <button 
              className={`sub-tab-button ${activeFunctionTab === 'display' ? 'active' : ''}`}
              onClick={() => handleFunctionTabChange('display')}
            >
              データ表示
            </button>
            
            {/* CSV Download Tab - Exporting data in CSV format */}
            <button 
              className={`sub-tab-button ${activeFunctionTab === 'download' ? 'active' : ''}`}
              onClick={() => handleFunctionTabChange('download')}
            >
              CSVダウンロード
            </button>
          </nav>
          
          {/* Function Tab Content Area - Renders active function component */}
          <div className="sub-tab-content">
            {/* Data Upload Tab Panel */}
            {activeFunctionTab === 'upload' && (
              <div className="sub-tab-panel">
                {/* Conditionally render office or construction upload component */}
                {activeWorkerTab === 'office' ? (
                  <DataUpload />
                ) : (
                  <ConstructionDataUpload />
                )}
              </div>
            )}
            
            {/* Data Display Tab Panel */}
            {activeFunctionTab === 'display' && (
              <div className="sub-tab-panel">
                {/* Conditionally render office or construction display component */}
                {activeWorkerTab === 'office' ? (
                  <DataDisplay />
                ) : (
                  <ConstructionDataDisplay />
                )}
              </div>
            )}
            
            {/* CSV Download Tab Panel */}
            {activeFunctionTab === 'download' && (
              <div className="sub-tab-panel">
                {/* Conditionally render office or construction download component */}
                {activeWorkerTab === 'office' ? (
                  <CsvDownload />
                ) : (
                  <ConstructionCsvDownload />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}